import React, { useEffect, useMemo, useState } from 'react';
import { Grid, TextField, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import dialogStyles from '../../../../components/dialog/GenericDialog.styles';
import ValidatedField from '../../../../components/inputs/ValidatedField';
import useAuthUser from '../../../auth/hooks/useAuthUser';
import { coerceFaulty, formatName, generateFileName, formatToDDMMYYYY } from '../../utils/meter.helpers';
import { useModifyMeter } from '../../hooks/useMeterActions';
import { METER_DATE_RULES, METER_NUMBER_RULES, METER_COMMENTS_RULES } from '../../utils/meterValidation';

export default function MeterModifyTab({ initialData, onSubmit, helpers }) {
  const { close, setLoading } = helpers;
  const [newMeterNumber, setNewMeterNumber] = useState('');
  const [newFromDate, setNewFromDate] = useState('');
  const [comments, setComments] = useState('');
  const navigate = useNavigate();

  const { user } = useAuthUser();
  const displayUser = useMemo(
    () => formatName(user?.name) || user?.email || 'Unknown User',
    [user]
  );

  const initialFaulty = coerceFaulty(
    initialData?.faulty ?? initialData?.Faulty ?? initialData?.IsFaulty ?? false
  );

  const oldMeterNumber = String(initialData?.meterNumber ?? '').trim();
  const newMeterTrim = newMeterNumber.trim();
  const sameAsOld =
    newMeterTrim !== '' &&
    oldMeterNumber.toLowerCase() === newMeterTrim.toLowerCase();

  const { mutate, loading } = useModifyMeter();

  useEffect(() => {
    setNewMeterNumber('');
    setNewFromDate('');
    setComments('');
    setLoading(false);
  }, [setLoading, initialData?.meterNumber, initialData?.fromDate]);

  const validMeterNumber = METER_NUMBER_RULES.every((r) => r.test(newMeterNumber));
  const validFromDate = METER_DATE_RULES.every((r) => r.test(newFromDate));
  const validComments = METER_COMMENTS_RULES.every((r) => r.test(comments));
  const canCreate = validMeterNumber && validFromDate && validComments && !sameAsOld;

  const handleCreate = async () => {
    if (!canCreate) return;
    setLoading(true);
    const filename = generateFileName();

    try {
      await mutate({
        reqType: 'MC',
        oldMeter: initialData?.meterNumber,
        oldFromDate: initialData?.fromDate,
        newMeter: newMeterTrim,
        selectedDate: newFromDate,
        comments: comments.trim() || null,
        faulty: initialFaulty,
        user: displayUser,
        filename,
      });
      onSubmit?.();
      close();
      navigate('/home/structure-logs', { state: { fileName: filename, status: null } });
    } catch (err) {
      alert(`Failed to record meter change:\n${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formattedFromDate = formatToDDMMYYYY(initialData?.fromDate);

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Old Meter Number"
            fullWidth
            size="small"
            variant="standard"
            value={oldMeterNumber}
            InputProps={{ readOnly: true, disableUnderline: true }}
            sx={dialogStyles.readonlyField}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Old From Date"
            fullWidth
            size="small"
            type="text"
            variant="standard"
            value={formattedFromDate}
            InputProps={{ readOnly: true, disableUnderline: true }}
            InputLabelProps={{ shrink: true }}
            sx={dialogStyles.readonlyField}
          />
        </Grid>

        <Grid item xs={12}>
          <ValidatedField
            label="New Meter Number"
            placeholder={oldMeterNumber}
            value={newMeterNumber}
            onChange={(e) => setNewMeterNumber(e.target.value)}
            rules={METER_NUMBER_RULES}
            externalError={
              sameAsOld ? 'New meter number must be different from old meter number.' : ''
            }
          />
        </Grid>

        <Grid item xs={12}>
          <ValidatedField
            label="New From Date"
            type="date"
            value={newFromDate}
            onChange={(e) => setNewFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            rules={METER_DATE_RULES}
          />
        </Grid>

        <Grid item xs={12}>
          <ValidatedField
            label="Comments"
            multiline
            placeholder="Enter comments..."
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rules={METER_COMMENTS_RULES}
          />
        </Grid>
      </Grid>

      <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
        <Button onClick={close} variant="contained" sx={dialogStyles.btnSecondary} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleCreate}
          disabled={!canCreate || loading}
          variant="contained"
          sx={dialogStyles.btnPrimary}
        >
          {loading ? 'Creating…' : 'Create'}
        </Button>
      </div>
    </>
  );
}
